import React, { useCallback, useMemo, useState } from 'react'
import {
  Box,
  Toolbar,
  AppBar as MuiAppBar,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Stack,
  ListItemText,
  ListItemIcon,
  Backdrop,
  CircularProgress,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import airLogo from 'assets/images/logo-air.png'
import { Link } from 'react-router-dom'
import axios from 'axios'
import config from 'config'
import logger from 'logger'

import MainToolbarLinks from 'components/atoms/MainToolbarLinks'
import IconButton from 'components/atoms/IconButton'
import LanguageItIcon from 'components/atoms/icons/LanguageItIcon'
import LanguageEnIcon from 'components/atoms/icons/LanguageEnIcon'
import LanguageDeIcon from 'components/atoms/icons/LanguageDeIcon'
import LanguageFrIcon from 'components/atoms/icons/LanguageFrIcon'

import { ROLES_SIXT, useAuth } from '../../auth'

const classes = {
  title: 'title',
  airLogo: 'air-logo',
}

const StyledMuiAppBar = styled(MuiAppBar)(({ theme }) => ({
  [`& .${classes.title}`]: {
    paddingLeft: theme.spacing(2.5),
    paddingRight: theme.spacing(2.5),
    borderLeft: '1px solid',
    borderColor: theme.palette.text.hint,
  },
  [`& .${classes.airLogo}`]: {
    paddingRight: theme.spacing(2.5),
    maxWidth: '66px',
  },
}))

const createLanguageMenuItems = (t) => [
  {
    Icon: LanguageItIcon,
    code: 'it',
    label: t('users.languages.it'),
  },
  {
    Icon: LanguageEnIcon,
    code: 'en',
    label: t('users.languages.en'),
  },
  {
    Icon: LanguageDeIcon,
    code: 'de',
    label: t('users.languages.de'),
  },
  {
    Icon: LanguageFrIcon,
    code: 'fr',
    label: t('users.languages.fr'),
  },
]

export default function AppBar({ authData, authState }) {
  const { t } = useTranslation()
  const {
    signOut,
    currentSession,
    hasOneOfRoles,
    language,
    refreshToken,
    updateAuth,
  } = useAuth()

  const [anchorEl, setAnchorEl] = useState(null)
  const [openLanguageMenu, setOpenLanguageMenu] = useState(false)
  const [loadingLanguage, setLoadingLanguage] = useState(false)

  const admin = useMemo(() => hasOneOfRoles(ROLES_SIXT), [hasOneOfRoles])

  const languages = useMemo(() => createLanguageMenuItems(t), [t])

  const toggleMenu = useCallback(
    (e) => {
      setAnchorEl(anchorEl ? null : e.currentTarget)
      setOpenLanguageMenu(false)
    },
    [anchorEl]
  )

  const logout = () => signOut()

  const showLogged = !(authState && authState === 'signIn')

  const onChangeLanguage = useCallback(
    async (newLanguage) => {
      toggleMenu()
      if (showLogged && !!currentSession) {
        setLoadingLanguage(true)
        try {
          await axios.patch(`${config.apiBaseUrl}users/language`, {
            language: newLanguage,
          })
          await refreshToken()
        } catch (e) {
          logger.error('[Error] - language change', e)
        }
        setLoadingLanguage(false)
      } else {
        localStorage.setItem('language', newLanguage)
        updateAuth()
      }
    },
    [currentSession, refreshToken, showLogged, toggleMenu, updateAuth]
  )

  const toggleLanguageMenu = () => {
    setOpenLanguageMenu((s) => !s)
  }

  const currentLanguage = useMemo(
    () => languages.find((lng) => lng.code === language) ?? languages[0],
    [language, languages]
  )

  return (
    <StyledMuiAppBar position="fixed" color="secondary">
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Stack direction="row" alignItems="center">
          <Box
            component="img"
            src={airLogo}
            alt={t('common.airName')}
            title={t('common.airName')}
            className={classes.airLogo}
          />
          <Divider orientation="vertical" variant="middle" />
          <Typography
            variant="body2"
            className={classes.title}
            color="textSecondary"
          >
            <strong>{t('common.appName')}</strong>
          </Typography>
        </Stack>
        {showLogged && <MainToolbarLinks />}
        <Stack direction="row" alignItems="center">
          <div>
            <IconButton
              color="secondary"
              onClick={toggleMenu}
              aria-label="account button"
              size="large"
              sx={{ ml: 2, p: 0.75 }}
              data-testid="button-account"
            >
              {showLogged ? (
                <AccountCircleOutlinedIcon />
              ) : (
                <currentLanguage.Icon fontSize="small" />
              )}
            </IconButton>
            <Menu
              id="appbar-menu"
              anchorEl={anchorEl}
              keepMounted
              open={!!anchorEl}
              onClose={toggleMenu}
            >
              {!!currentSession && !admin && (
                <MenuItem
                  component={Link}
                  to="/user-documents"
                  onClick={toggleMenu}
                  data-testid="action-partner-documents"
                >
                  {t('menu.dropdown.partnerDocuments')}
                </MenuItem>
              )}

              <MenuItem onClick={toggleLanguageMenu}>
                <ListItemText>{t('menu.dropdown.changeLanguage')}</ListItemText>
                <ListItemIcon>
                  <currentLanguage.Icon
                    fontSize="small"
                    sx={{ minWidth: 2.5, pl: 2 }}
                  />
                </ListItemIcon>
              </MenuItem>
              {openLanguageMenu &&
                languages.map((lng) => (
                  <MenuItem
                    key={`language_${lng.code}`}
                    onClick={() => onChangeLanguage(lng.code)}
                  >
                    <Box pr={2} />
                    <Box display="flex">
                      <ListItemText>{lng.label}</ListItemText>
                      <ListItemIcon>
                        <lng.Icon
                          fontSize="small"
                          sx={{ minWidth: 2.5, pl: 2 }}
                        />
                      </ListItemIcon>
                    </Box>
                  </MenuItem>
                ))}
              {!!currentSession && (
                <MenuItem
                  onClick={logout}
                  data-testid="action-logout"
                  aria-label="action logout"
                >
                  {t('menu.dropdown.logout')}
                </MenuItem>
              )}
            </Menu>
          </div>
        </Stack>
      </Toolbar>
      <Backdrop
        invisible
        sx={(theme) => ({
          zIndex: theme.zIndex.drawer + 1,
        })}
        open={loadingLanguage}
      >
        <CircularProgress />
      </Backdrop>
    </StyledMuiAppBar>
  )
}

AppBar.propTypes = {
  authData: T.shape({
    username: T.string,
  }),
  authState: T.string,
}
